import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import Delete from '@mui/icons-material/Delete'
import Add from '@mui/icons-material/Add'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import moment from 'moment'
import API from '../../../../services/API'

const AppraisalReview = ({ appraisalID, onCompleted }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [appraisal, setAppraisal] = useState(null)
  const [reviewerQuestions, setReviewerQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [actions, setActions] = useState([])

  useEffect(() => {
    Promise.all([
      API.get(`/appraisals/${appraisalID}`),
      API.get('/settings?select=staffAppraisalSettings'),
    ]).then(([appraisalRes, settingsRes]) => {
      if (appraisalRes.status === 200) {
        setAppraisal(appraisalRes.data)
      }
      if (settingsRes.status === 200) {
        const questions =
          settingsRes.data?.staffAppraisalSettings?.appraisalQuestions ?? []
        setReviewerQuestions(questions)
        setAnswers(
          Object.fromEntries(
            questions
              .filter((q) => q.section === 'reviewer')
              .map((q) => [q.key, '']),
          ),
        )
      }
      setLoading(false)
    })
  }, [appraisalID])

  const promptFor = (key) =>
    reviewerQuestions.find((q) => q.key === key)?.prompt ?? key

  const addAction = () => {
    setActions((prev) => [
      ...prev,
      { title: '', dueDate: '', assignedToTeam: 'Personnel' },
    ])
  }

  const updateAction = (index, changes) => {
    setActions((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...changes } : a)),
    )
  }

  const removeAction = (index) => {
    setActions((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    setSaving(true)
    API.put(`/appraisals/${appraisalID}/review`, {
      answers: Object.entries(answers).map(([key, answer]) => ({
        key,
        question: promptFor(key),
        answer,
      })),
      actions: actions.filter((a) => a.title?.trim()),
    })
      .then((res) => {
        if (res.status === 200) {
          onCompleted?.(res.data)
        }
      })
      .finally(() => setSaving(false))
  }

  if (loading || !appraisal) return <LinearProgress />

  const employeeUserID = appraisal.employee?.userAccount?._id

  return (
    <Stack gap={2}>
      <Typography variant="title2">
        Appraisal Review — {appraisal.employee?.userAccount?.firstName}{' '}
        {appraisal.employee?.userAccount?.lastName}
      </Typography>
      <Chip label={appraisal.status} sx={{ alignSelf: 'flex-start' }} />

      <Card variant="outlined">
        <CardHeader
          title={promptFor('mandatoryTraining')}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          {appraisal.preview?.mandatoryTrainingSnapshot?.hasExpiredOrMissing ? (
            <Chip color="error" label="Training overdue or missing" />
          ) : (
            <Chip color="success" label="Training up to date" />
          )}
          <Stack gap={0.5} mt={1}>
            {(
              appraisal.preview?.mandatoryTrainingSnapshot?.requiredCourses ??
              []
            ).map((course) => {
              const completed =
                appraisal.preview?.mandatoryTrainingSnapshot?.completedCourses?.find(
                  (c) => c.courseId === course.courseId,
                )
              return (
                <Typography variant="body2" key={course.courseId}>
                  {course.title}:{' '}
                  {completed
                    ? moment(completed.date).format('Do MMM YYYY')
                    : 'Not completed'}
                </Typography>
              )
            })}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={promptFor('fitnessToPractice')}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack gap={1} mb={2}>
            {[
              ...(appraisal.preAppraisal?.fitnessToPractice ?? []),
              ...(appraisal.preAppraisal?.occupationalHealth ?? []),
            ].map((item, i) => (
              <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                <Chip
                  size="small"
                  label={item.answer || 'No answer'}
                  color={
                    !item.answer
                      ? 'default'
                      : item.answer === item.flagOn
                        ? 'error'
                        : 'success'
                  }
                />
                <Typography variant="body2">
                  {item.question}
                  {item.comment && (
                    <>
                      {' — '}
                      <i>{item.comment}</i>
                    </>
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Your notes"
            value={answers.fitnessToPractice ?? ''}
            onChange={(e) =>
              setAnswers((prev) => ({
                ...prev,
                fitnessToPractice: e.target.value,
              }))
            }
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={promptFor('complaintsCompliments')}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack gap={1} mb={2}>
            {(appraisal.preview?.linkedFeedback?.feedback ?? []).map((f) => (
              <Typography variant="body2" key={f._id}>
                {f.isComplement ? 'Compliment' : 'Complaint'} (
                {moment(f.createdAt).format('Do MMM YYYY')}):{' '}
                {f.feedbackComments}
              </Typography>
            ))}
            {(appraisal.preview?.linkedFeedback?.shoutOuts ?? []).map((s) => (
              <Typography variant="body2" key={s._id}>
                Shout-out ({moment(s.createdAt).format('Do MMM YYYY')}):{' '}
                {s.shoutOut}
              </Typography>
            ))}
            {!appraisal.preview?.linkedFeedback?.feedback?.length &&
              !appraisal.preview?.linkedFeedback?.shoutOuts?.length && (
                <Typography variant="body2" color="text.secondary">
                  No feedback recorded for this employee this period.
                </Typography>
              )}
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Your notes"
            value={answers.complaintsCompliments ?? ''}
            onChange={(e) =>
              setAnswers((prev) => ({
                ...prev,
                complaintsCompliments: e.target.value,
              }))
            }
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title="Employee Self-Review"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <Stack gap={1}>
            {(appraisal.employeeSection?.answers ?? []).map((a, i) => (
              <Typography variant="body2" key={i}>
                <b>{a.question}</b> — {a.answer || 'No answer given'}
              </Typography>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={promptFor('ourFeedback')}
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={answers.ourFeedback ?? ''}
            onChange={(e) =>
              setAnswers((prev) => ({ ...prev, ourFeedback: e.target.value }))
            }
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={promptFor('actionPlan')}
          subheader="Create clear follow-up tasks for the employee or Personnel Team."
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
          subheaderTypographyProps={{ variant: 'body2' }}
        />
        <Divider />
        <CardContent>
          <Stack gap={1.5}>
            {actions.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'action.hover',
                }}
              >
                <Stack direction="row" gap={1.5} alignItems="center">
                  <AssignmentTurnedInIcon color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      No actions added yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add an action when there is a task to follow up after the
                      appraisal.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
            {actions.map((action, i) => (
              <Paper
                variant="outlined"
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: 'divider',
                }}
              >
                <Stack gap={1.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Action {i + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      size="small"
                      aria-label={`Remove action ${i + 1}`}
                      onClick={() => removeAction(i)}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={1.5}
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                  >
                    <TextField
                      label="Action"
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      value={action.title}
                      onChange={(e) =>
                        updateAction(i, { title: e.target.value })
                      }
                    />
                    <Select
                      size="small"
                      value={action.assignedToTeam ? 'team' : 'employee'}
                      displayEmpty
                      sx={{
                        minWidth: { xs: '100%', md: 190 },
                      }}
                      onChange={(e) =>
                        updateAction(
                          i,
                          e.target.value === 'team'
                            ? {
                                assignedToTeam: 'Personnel',
                                assignedToUser: undefined,
                              }
                            : {
                                assignedToTeam: undefined,
                                assignedToUser: employeeUserID,
                              },
                        )
                      }
                    >
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="team">Personnel Team</MenuItem>
                    </Select>
                    <TextField
                      label="Due date"
                      size="small"
                      type="date"
                      value={action.dueDate}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <CalendarTodayIcon
                            color="action"
                            fontSize="small"
                            sx={{ mr: 0.75 }}
                          />
                        ),
                      }}
                      sx={{
                        minWidth: { xs: '100%', md: 190 },
                      }}
                      onChange={(e) =>
                        updateAction(i, { dueDate: e.target.value })
                      }
                    />
                  </Stack>
                </Stack>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addAction}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Action
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        color="success"
        size="large"
        onClick={handleSubmit}
        disabled={saving || appraisal.status === 'Completed'}
      >
        Complete Appraisal
      </Button>
    </Stack>
  )
}

export default AppraisalReview
